/** Toma periodos de respuesta. */
export function periodList(res: any): any[] {
  return (res.periods ?? []) as any[];
}

/** Toma entidades de respuesta. */
export function entityList(res: any): any[] {
  return (res.entities ?? []) as any[];
}

/** Insignias por estado. */
export const STATUS_BADGES: Record<string, string> = {
  'Abierto': 'bg-success',
  'Vencido': 'bg-warning text-dark',
  'Sin periodo': 'bg-light text-dark border',
};

/** Indica si el periodo expiró. */
export function hasExpired(row: { expiration?: unknown }): boolean {
  return new Date(row.expiration as string).getTime() < Date.now();
}

/** Calcula estado de periodo. */
export function periodStatusOf(row: { enabled?: boolean; expiration?: unknown }): string {
  if (isDisabledPeriod(row) === true) return 'Cerrado';
  return statusByExpiry(row);
}

/** Resuelve estado por expiración. */
export function statusByExpiry(row: { expiration?: unknown }): string {
  if (hasExpired(row) === true) return 'Vencido';
  return 'Abierto';
}

/** Resuelve etiqueta de estado. */
export function statusLabel(row: { noPeriod?: boolean; enabled?: boolean; expiration?: unknown }): string {
  if (row.noPeriod) return 'Sin periodo';
  return periodStatusOf(row);
}

/** Resuelve insignia de estado. */
export function statusBadgeOf(row: { noPeriod?: boolean; enabled?: boolean; expiration?: unknown }): string {
  const status = statusLabel(row);
  if (STATUS_BADGES[status]) return STATUS_BADGES[status];
  return 'bg-secondary';
}

/** Indexa periodos por entidad. */
export function indexByEntity(periods: any[]): Map<string, any> {
  const map = new Map<string, any>();
  for (const p of periods) map.set(p.entityId, p);
  return map;
}

/** Crea fila sin periodo. */
export function rowWithoutPeriod(entity: any): Record<string, unknown> {
  return { id: 'ent-' + entity.id, entity, entityId: entity.id, noPeriod: true };
}

/** Toma fila por entidad o vacía. */
export function rowForEntity(byEntity: Map<string, any>, entity: any): unknown {
  const found = byEntity.get(entity.id);
  if (found) return found;
  return rowWithoutPeriod(entity);
}

/** Construye filas periodo-entidad. */
export function buildPeriodRows(periods: any[], entities: any[]): unknown[] {
  const byEntity = indexByEntity(periods);
  const rows: unknown[] = [];
  for (const e of entities) rows.push(rowForEntity(byEntity, e));
  return rows;
}

/** Toma módulos de grupo. */
export function groupModulesOf(module: { groupModules?: unknown }): unknown[] {
  return (module?.groupModules ?? []) as unknown[];
}

/** Enlaza grupo de módulo. */
export function linkGroupModule(gm: { group?: unknown }): unknown {
  return gm?.group ?? null;
}

/** Indica si el valor existe. */
export function isPresent(value: unknown): boolean {
  return Boolean(value);
}

/** Lista grupos del módulo. */
export function groupsOf(module: { groupModules?: unknown }): unknown[] {
  return (groupModulesOf(module) as Array<{ group?: unknown }>).map(linkGroupModule).filter(isPresent);
}

/** Toma primer grupo o nulo. */
export function firstGroup(list: unknown[]): unknown {
  if (list.length === 0) return null;
  return list[0];
}

/** Toma ids de filas. */
export function collectIds(rows: Array<{ id: string }>): string[] {
  return rows.map(rowId);
}

/** Toma id de fila. */
export function rowId(row: { id: string }): string {
  return row.id;
}

/** Toma ids de filas abiertas. */
export function collectOpenIds(rows: Array<{ id: string }>, isOpen: (row: { id: string }) => boolean): string[] {
  return rows.filter(isOpen).map(rowId);
}

/** Indica si el periodo está deshabilitado. */
export function isDisabledPeriod(row: { enabled?: boolean }): boolean {
  return row.enabled !== true;
}

/** Indica si puede abrir periodos. */
export function canOpenPeriods(module: unknown, group: unknown): boolean {
  return Boolean(module) && Boolean(group);
}

/** Indica si es id de periodo real. */
export function isPeriodId(id: string): boolean {
  return id.startsWith('ent-') === false;
}

/** Filtra ids reales. */
export function periodRealIds(ids: string[]): string[] {
  return ids.filter(isPeriodId);
}

/** Toma ids seleccionados. */
export function selectedIdList(selected: Set<string>): string[] {
  return [...selected];
}

/** Toma filas seleccionadas. */
export function selectedRows(rows: Array<{ id: string }>, selected: Set<string>): Array<{ id: string }> {
  return rows.filter((row) => selected.has(row.id));
}

/** Toma entidad cruda de fila. */
export function rowEntity(row: { entityId?: string }): string {
  return row.entityId as string;
}

/** Toma ids de entidad para reapertura. */
export function reopenEntityIds(rows: Array<{ entityId?: string }>): string[] {
  return rows.map(rowEntity).filter(isPresent) as string[];
}

/** Toma ids únicos para reinicio. */
export function resetEntityIds(rows: Array<{ entityId?: string }>): string[] {
  return uniqueTexts(reopenEntityIds(rows));
}

/** Deduplica textos. */
export function uniqueTexts(list: string[]): string[] {
  return [...new Set(list)];
}

/** Indica si la lista está vacía. */
export function isEmptyList(list: unknown[]): boolean {
  return list.length === 0;
}

/** Indica si no hay periodo. */
export function isNoPeriodRow(row: { noPeriod?: boolean }): boolean {
  return row.noPeriod === true;
}

/** Indica si es fila revisable. */
export function isReviewable(row: { noPeriod?: boolean; id?: string; realized?: unknown }): boolean {
  return hasReviewData(row) === true;
}

/** Revisa datos de revisión. */
export function hasReviewData(row: { noPeriod?: boolean; id?: string; realized?: unknown }): boolean {
  return Boolean(row.id) && Boolean(row.realized) && row.noPeriod !== true;
}

/** Arma texto de cierre masivo. */
export function closeManyText(count: number): string {
  return `Se cerrarán <b>${count}</b> periodo(s). Ya no podrán recibir respuestas.`;
}

/** Arma texto de reinicio. */
export function resetText(count: number, month: string): string {
  return `Se reiniciarán <b>${count}</b> entidad(es) desde 0 para <b>${month}</b>. Se perderá <b>todo su avance</b> y se crearán nuevamente las preguntas del módulo. Esta acción no se puede deshacer.`;
}

/** Extrae mensaje de resultado. */
export function resultMessage(result: { message?: unknown }, fallback: string): string {
  if (result?.message) return String(result.message);
  return fallback;
}

/** Extrae texto de error de carga. */
export function loadErrorText(e: { status?: unknown; error?: { error?: unknown } }): string {
  return `No se pudieron cargar los datos (${loadStatus(e)}). ${loadDetail(e)}`;
}

/** Toma estado de error. */
export function loadStatus(e: { status?: unknown }): string {
  return String(e.status ?? 'sin estado');
}

/** Toma detalle de error. */
export function loadDetail(e: { error?: { error?: unknown } }): string {
  return String(e.error?.error ?? '');
}

/** Toma error de diálogo. */
export function dialogErrorText(e: { error?: { error?: unknown } }): string | undefined {
  return e.error?.error as string | undefined;
}
